import { Injectable } from '@angular/core';

import { environment } from '../../environments/environment';
import { SESSION_AUTH_KEY, SESSION_TENANT_CONTEXT_KEY } from './storage-keys';

/**
 * AuthService — owns the operator's PKCE token state.
 *
 * Primary storage: **sessionStorage** under the `SESSION_AUTH_KEY` key
 * (see services/storage-keys.ts). Persistent across page refresh within
 * the same tab; discarded on tab close.
 *
 * Trade-offs:
 *   - Surface area: any script running on the admin-ui origin can read
 *     the tokens. Mitigations: the CSP at
 *     `infra/main.tf:aws_cloudfront_response_headers_policy.admin_ui_security_headers`
 *     restricts `script-src 'self'` (no third-party JS loaded; no
 *     inline-script / eval surfaces in the SPA).
 *   - Persistence: tab-scoped, not cross-tab and not across browser
 *     restarts. Tab close = re-signin.
 *
 * Fallback: if `sessionStorage.setItem` throws (Safari Private mode,
 * quota exceeded), the bundle is held in a private in-memory field and
 * the operator stays signed in for the current tab; refresh reverts to
 * the pre-RP8 "lose-session" behaviour.
 *
 * Refresh-token redemption (post-RP8c): when the admin grant's scope
 * includes `offline_access`, the backend mints a `refresh_token`
 * alongside the access_token. `refresh()` below POSTs to /oauth/token
 * (grant_type=refresh_token) to mint a fresh access_token (+ rotated
 * refresh_token) without re-prompting for password. Used by the
 * adminApiInterceptor's reactive-401-retry: a 401 from /admin/* tries
 * refresh first; on success the original request is replayed with the
 * new Bearer; on failure the existing logout+/signin flow runs.
 *
 * Single-flight guard: `refreshInFlight` coalesces concurrent
 * refresh() calls onto the same promise. Without it, N parallel admin
 * XHRs all 401'ing in the same tick would each fire a refresh, the
 * backend rotates the refresh_token on each, and N-1 of the rotations
 * would be wasted (and possibly invalidate each other).
 */

interface StoredSession {
  accessToken: string;
  refreshToken: string;
  idToken: string;
  expiresAtMs: number;
  // `clientId` is needed for the refresh-token POST body (per RFC 6749
  // §6 the request body is grant_type=refresh_token&refresh_token=…&
  // client_id=…). Extracted from the access_token JWT in
  // SigninComponent.completeSignin() and passed into setTokens().
  clientId: string;
}

interface RefreshResponseShape {
  access_token?: string;
  refresh_token?: string;
  id_token?: string;
  expires_in?: number;
  token_type?: string;
  scope?: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private static readonly CLOCK_SKEW_MS = 30_000;
  private static readonly MAX_SESSION_DURATION_MS = 24 * 60 * 60 * 1000;

  private fallbackSession: StoredSession | null = null;
  private refreshInFlight: Promise<boolean> | null = null;

  setTokens(
    accessToken: string,
    refreshToken: string,
    idToken: string,
    expiresInSec: number,
    clientId: string,
  ): void {
    if (!Number.isFinite(expiresInSec) || expiresInSec <= 0) {
      throw new Error(`AuthService.setTokens: invalid expiresInSec=${expiresInSec}`);
    }
    if (typeof clientId !== 'string' || clientId === '') {
      throw new Error('AuthService.setTokens: clientId is required (post-RP8c — needed for refresh-token redemption)');
    }

    const session: StoredSession = {
      accessToken,
      refreshToken,
      idToken,
      expiresAtMs: Date.now() + expiresInSec * 1000,
      clientId,
    };
    this.fallbackSession = session;
    try {
      sessionStorage.setItem(SESSION_AUTH_KEY, JSON.stringify(session));
    } catch {
      // sessionStorage may be unavailable; fallbackSession keeps the
      // current tab functional. Refresh loses the session (pre-RP8 behaviour).
    }
  }

  currentAccessToken(): string | null {
    return this.readSession()?.accessToken ?? null;
  }

  currentIdToken(): string | null {
    return this.readSession()?.idToken ?? null;
  }

  isAuthenticated(): boolean {
    const session = this.readSession();
    if (session === null) {
      return false;
    }
    if (session.expiresAtMs <= 0) {
      return false;
    }
    return Date.now() < session.expiresAtMs - AuthService.CLOCK_SKEW_MS;
  }

  /**
   * Post-RP8c — refresh the access_token via /oauth/token redemption.
   *
   * Returns true if the refresh succeeded and the stored session has
   * been updated with the new tokens; false if the refresh failed or
   * there's no refresh_token to redeem (grandfathered pre-RP8c grants).
   *
   * Single-flight: concurrent callers receive the same in-flight promise.
   * Uses `fetch()` directly (not Angular's HttpClient) to:
   *   1. avoid this request being intercepted by adminApiInterceptor
   *      (which would attempt to attach Bearer + handle 401 — neither
   *      appropriate for the refresh path itself);
   *   2. avoid an HttpClient ↔ AuthService dependency that complicates
   *      Angular's injector graph.
   */
  refresh(): Promise<boolean> {
    if (this.refreshInFlight !== null) {
      return this.refreshInFlight;
    }
    const session = this.readSession();
    if (session === null || session.refreshToken === '' || session.clientId === '') {
      // Nothing to refresh — caller will fall back to logout flow.
      return Promise.resolve(false);
    }
    this.refreshInFlight = this.doRefresh(session);
    // Clear the in-flight slot regardless of outcome, so a later call
    // can fire a fresh refresh attempt.
    this.refreshInFlight.finally(() => {
      this.refreshInFlight = null;
    });
    return this.refreshInFlight;
  }

  private async doRefresh(session: StoredSession): Promise<boolean> {
    // In local-dev mode (environment.oauthBaseUrl = '') the relative
    // path is proxied to the deployed dev backend by proxy.conf.js.
    // In production the full URL is constructed against the API origin.
    const url = `${environment.oauthBaseUrl}/oauth/token`;

    // application/x-www-form-urlencoded per RFC 6749 §6 — the canonical
    // OAuth wire format for /token. The backend/token Lambda's
    // parseTokenRequest accepts both urlencoded AND JSON (post the
    // dual-format change shipped alongside this); urlencoded is the
    // spec-correct format so SPAs + third-party integrations following
    // the standard all work without surprise.
    const body = new URLSearchParams();
    body.set('grant_type', 'refresh_token');
    body.set('refresh_token', session.refreshToken);
    body.set('client_id', session.clientId);

    let response: Response;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
    } catch {
      // Network failure — refresh is dead in the water.
      return false;
    }
    if (!response.ok) {
      // 4xx (invalid_grant / expired / revoked) or 5xx — give up on
      // this session; caller redirects to /signin.
      return false;
    }
    let parsed: RefreshResponseShape;
    try {
      parsed = (await response.json()) as RefreshResponseShape;
    } catch {
      return false;
    }
    // ROTATION SEMANTICS — the backend ALWAYS rotates when the scope
    // includes `offline_access` (see backend/token/refreshToken.go:79+85
    // — `redeemRefreshToken` marks the old as REDEEMED *before* the new
    // one is minted). By the time this response lands, the OLD refresh
    // token in `session.refreshToken` is already invalidated server-side.
    //
    // Therefore `refresh_token` is REQUIRED in the response. If it's
    // missing or malformed:
    //   - keeping the old one would guarantee the next refresh attempt
    //     fails with invalid_grant (operator gets bounced to /signin
    //     for no visible reason);
    //   - it would also break the canonical refresh-token-theft-detection
    //     chain (the OWASP pattern requires the client to use the new
    //     token immediately so a replayed OLD token is the theft signal).
    //
    // Treat missing/non-string refresh_token as malformed → refresh
    // failed → caller (interceptor) runs the logout + /signin flow.
    if (
      typeof parsed.access_token !== 'string' ||
      typeof parsed.expires_in !== 'number' ||
      typeof parsed.refresh_token !== 'string'
    ) {
      return false;
    }
    // OIDC contract — id_token is OPTIONAL on the refresh response.
    // The backend's RefreshTokenStrategy only mints a new access_token
    // (+ rotated refresh_token); id tokens carry stable identity and
    // don't get re-issued on refresh. If the response omits id_token,
    // keep the existing one from the original sign-in — it remains
    // valid for the operator's identity (sub + email + tenant context).
    const nextIdToken =
      typeof parsed.id_token === 'string' && parsed.id_token !== ''
        ? parsed.id_token
        : session.idToken;
    try {
      this.setTokens(parsed.access_token, parsed.refresh_token, nextIdToken, parsed.expires_in, session.clientId);
    } catch {
      // expires_in <= 0 / NaN — backend response is malformed.
      return false;
    }
    return true;
  }

  logout(): void {
    this.fallbackSession = null;
    try {
      sessionStorage.removeItem(SESSION_AUTH_KEY);
      sessionStorage.removeItem(SESSION_TENANT_CONTEXT_KEY);
    } catch {
      /* best-effort */
    }
    try {
      const keys: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith('admin_client_id_')) {
          keys.push(k);
        }
      }
      for (const k of keys) {
        localStorage.removeItem(k);
      }
      localStorage.removeItem('admin_last_tenant_id');
    } catch {
      /* best-effort */
    }
  }

  private readSession(): StoredSession | null {
    const stored = this.readFromStorage();
    if (stored !== null) {
      return stored;
    }
    return this.fallbackSession;
  }

  private readFromStorage(): StoredSession | null {
    let raw: string | null;
    try {
      raw = sessionStorage.getItem(SESSION_AUTH_KEY);
    } catch {
      return null;
    }
    if (raw === null) {
      return null;
    }
    let parsed: Partial<StoredSession>;
    try {
      parsed = JSON.parse(raw) as Partial<StoredSession>;
    } catch {
      return null;
    }
    if (
      typeof parsed.accessToken !== 'string' ||
      typeof parsed.refreshToken !== 'string' ||
      typeof parsed.idToken !== 'string' ||
      typeof parsed.expiresAtMs !== 'number' ||
      !Number.isFinite(parsed.expiresAtMs) ||
      typeof parsed.clientId !== 'string'
    ) {
      return null;
    }
    if (parsed.expiresAtMs > Date.now() + AuthService.MAX_SESSION_DURATION_MS) {
      return null;
    }
    return {
      accessToken: parsed.accessToken,
      refreshToken: parsed.refreshToken,
      idToken: parsed.idToken,
      expiresAtMs: parsed.expiresAtMs,
      clientId: parsed.clientId,
    };
  }

  // --- PKCE crypto helpers (forward-compat; not on the active sign-in path) ---

  async generateCodeVerifier(): Promise<string> {
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    return base64UrlEncode(bytes);
  }

  async generateCodeChallenge(verifier: string): Promise<string> {
    const encoded = new TextEncoder().encode(verifier);
    const digest = await crypto.subtle.digest('SHA-256', encoded);
    return base64UrlEncode(new Uint8Array(digest));
  }

  generateOpaqueToken(): string {
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    return base64UrlEncode(bytes);
  }

  decodeJwtPayload<T = Record<string, unknown>>(jwt: string): T | null {
    const parts = jwt.split('.');
    if (parts.length !== 3) {
      return null;
    }
    try {
      return JSON.parse(base64UrlDecodeUtf8(parts[1])) as T;
    } catch {
      return null;
    }
  }

  /**
   * Story 26.3 — true when the current access token's space-separated `scope`
   * claim contains `scope`. Central home for UI scope-gating (e.g. `logs:read`
   * gating the Monitoring section + Logs tabs), consolidating what was a
   * copy-pasted per-component getter.
   */
  hasScope(scope: string): boolean {
    const token = this.currentAccessToken();
    if (!token) {
      return false;
    }
    const claims = this.decodeJwtPayload<{ scope?: string }>(token);
    return (claims?.scope ?? '').split(' ').includes(scope);
  }
}

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  for (const b of bytes) {
    binary += String.fromCharCode(b);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function base64UrlDecodeUtf8(input: string): string {
  const b64 = input.replace(/-/g, '+').replace(/_/g, '/');
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new TextDecoder('utf-8').decode(bytes);
}
